import React, {
    forwardRef,
    type ForwardRefRenderFunction,
    type ReactNode,
    type UIEvent,
    useEffect,
    useState,
} from 'react';

import WebGL from '@pkg/webgl';

import { Hidden, Inner, StyledScrollbars, Thumb, Track } from './styles';

const FULL_MASK_DISTANCE = 30;

interface Props {
    className?: string;
    children: ReactNode;
    maskTop?: boolean;
    trackOffset?: number;
    distanceToTriggerScrollEnd?: number;
    showBackground?: boolean;
    margin?: string;
    onScroll?: () => void;
    onScrollEnd?: () => void;
}

const YatScrollbar: ForwardRefRenderFunction<HTMLDivElement, Props> = (
    {
        className,
        children,
        maskTop = true,
        trackOffset = 0,
        onScroll,
        onScrollEnd,
        distanceToTriggerScrollEnd = 0,
        showBackground,
        margin,
    }: Props,
    ref,
) => {
    const [maskOpacity, setMaskOpacity] = useState(1);

    const handleScroll = (event: UIEvent) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

        if (WebGL.world?.layerManager) {
            WebGL.world.layerManager.setScrollFromElement(scrollTop, scrollHeight);
        }

        if (maskTop) {
            const distance = scrollTop > FULL_MASK_DISTANCE ? FULL_MASK_DISTANCE : scrollTop;
            const opacity = distance / FULL_MASK_DISTANCE;
            setMaskOpacity(1 - opacity);
        }

        if (onScroll) onScroll();

        if (onScrollEnd) {
            const maxScroll = scrollHeight - clientHeight;
            if (maxScroll - scrollTop <= distanceToTriggerScrollEnd) onScrollEnd();
        }
    };

    useEffect(() => {
        return () => {
            if (WebGL && WebGL.world?.layerManager) {
                WebGL.world.layerManager.unsetScrollFromElement();
            }
        };
    }, []);

    return (
        <StyledScrollbars
            ref={ref}
            renderView={() => <Inner className={className} $maskOpacity={maskOpacity} $margin={margin} />}
            renderTrackVertical={() => <Track $trackOffset={trackOffset} $showBackground={showBackground} />}
            renderThumbVertical={() => <Thumb />}
            renderTrackHorizontal={() => <Hidden />}
            renderThumbHorizontal={() => <Hidden />}
            autoHide
            onScroll={handleScroll}
        >
            {children}
        </StyledScrollbars>
    );
};

export default forwardRef(YatScrollbar);
